import { useCallback, useEffect, useState } from 'react';

/**
 * This function will count how many rows and groups are being displayed in agGrid.
 *
 * @param {*} gridApi
 * @returns { rowCount, groupCount } number of rows and number of groups
 */
function countGroupsAndListings(gridApi) {
  if (!gridApi || gridApi.destroyCalled) {
    return null; // solves race condition where this func is called after grid destroyed and deref'd
  }
  let rowCount = 0;
  let groupCount = 0;
  gridApi.forEachNodeAfterFilter((rowNode) => {
    if (rowNode.group) {
      groupCount += 1;
    } else {
      rowCount += 1;
    }
  });
  return { rowCount, groupCount };
}

/**
 * Use this hook whenever you need to get the counts for rows and rowGroups from AgGrid
 *
 * @param {*} gridApi
 * @returns { counts, onFilterChanged }
 *   counts: { rowCount, groupCount } tells you how many rows and how many groups
 *   onFilterChanged: pass this this callback to AgGrid (onFilterChanged prop). It will update
 *   the returned value `counts` every time there is a change in an Ag Grid filter
 */
export function useAgGridCounts(gridApi) {
  const [filterModel, setFilterModel] = useState(null);
  const [counts, setCounts] = useState(null);

  const setFilterModelCB = useCallback(() => {
    setFilterModel(gridApi.getFilterModel());
  }, [gridApi, setFilterModel]);

  const refreshCounts = useCallback(() => {
    if (!gridApi) return;
    const countsValue = countGroupsAndListings(gridApi);
    setCounts(countsValue);
  }, [gridApi, filterModel]);

  useEffect(() => {
    refreshCounts();
  }, [gridApi, filterModel]);

  return {
    onFilterChanged: setFilterModelCB,
    refreshCounts,
    counts,
  };
}
